/**
 * QZ Tray HTML pixel print ignores CSS page-break rules when scaleContent is true.
 * Split multipage packing/Cheetah/general HTML into one full document per physical page
 * so each QZ spool is truly one sheet.
 */

const PAGE_SELECTORS = [
  '.lz-sheet',
  '.packing-slip-page',
  '.go4-outside-page',
  '.gift-card',
  '.separator-page',
  '.wave-pick-page',
  '.print-separator',
  '.print-separator-sheet',
  '[data-print-page]',
];

/**
 * @param {string} fullHtml
 * @returns {string[]} one HTML document per physical page (or original if single/unknown)
 */
export function splitHtmlDocumentIntoQzPages(fullHtml) {
  const source = String(fullHtml || '');
  if (!source.trim()) return [source];
  if (typeof DOMParser === 'undefined') return [source];

  let doc;
  try {
    doc = new DOMParser().parseFromString(source, 'text/html');
  } catch {
    return [source];
  }

  let blocks = [];
  let usedSelector = '';
  for (const selector of PAGE_SELECTORS) {
    const found = Array.from(doc.querySelectorAll(selector));
    if (found.length > 1) {
      blocks = found;
      usedSelector = selector;
      break;
    }
  }

  // General calibration / letter uses a single .sheet; only split when many.
  if (!blocks.length) {
    const sheets = Array.from(doc.querySelectorAll('.sheet'));
    if (sheets.length > 1) {
      blocks = sheets;
      usedSelector = '.sheet';
    }
  }

  if (blocks.length <= 1) return [source];

  const headHtml = doc.head?.innerHTML || '';
  return blocks.map((block, index) => {
    const markup = block.cloneNode(true).outerHTML;
    return `<!DOCTYPE html>
<html>
<head>
${headHtml}
<style data-qz-single-page="true">
html, body { margin: 0; padding: 0; }
${usedSelector} {
  break-after: auto !important;
  page-break-after: auto !important;
  break-before: auto !important;
  page-break-before: auto !important;
}
</style>
</head>
<body data-qz-page-index="${index + 1}" data-qz-page-count="${blocks.length}">
${markup}
</body>
</html>`;
  });
}

/**
 * Expand HTML print jobs so each physical page is its own sequential QZ job.
 * @param {Array<{ html?: string, label?: string, id?: string, profileKey?: string, settings?: object, sequence?: number }>} jobs
 */
export function expandHtmlJobsForQzPages(jobs = []) {
  const expanded = [];
  (jobs || []).forEach((job, jobIndex) => {
    const pages = splitHtmlDocumentIntoQzPages(job?.html);
    pages.forEach((pageHtml, pageIndex) => {
      const baseLabel = job?.label || job?.profileKey || `job-${jobIndex + 1}`;
      expanded.push({
        ...job,
        html: pageHtml,
        id:
          pages.length > 1
            ? `${job?.id || job?.profileKey || `job-${jobIndex + 1}`}-p${pageIndex + 1}`
            : job?.id,
        label:
          pages.length > 1
            ? `${baseLabel} (${pageIndex + 1}/${pages.length})`
            : job?.label,
        sequence:
          job?.sequence != null
            ? Number(job.sequence) + pageIndex / 1000
            : jobIndex + pageIndex / 1000,
      });
    });
  });
  return expanded;
}

export default splitHtmlDocumentIntoQzPages;
